import { useState, useCallback, useEffect } from "react";
import useAuth from "../../shared/services/store/useAuth";
import { apidownloadPDF,  getOrderdetails, } from "../../shared/services/apiorder/apiorder";
import { PackageX } from "lucide-react";
import { Tooltip } from "@nextui-org/react";
import useCartStore from "../../shared/services/store/usecart";
import Cartsidebar from "../../shared/components/CheckoutandCart/Cartsidebar";
import toast from "react-hot-toast";
import { saveAs } from "file-saver";
import { apiTestseriesdownloadPDF, getUserOrders,} from "../../shared/services/apihomeTestseries/apihometestseries";
import { useNavigate } from "react-router-dom";

export default function MyOrders() {
  const [orderDetails, setOrderDetails] = useState([]);
  const [testSeriesOrders, setTestSeriesOrders] = useState([]);
  const { userdetails } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState({});
  const { cart } = useCartStore();
  const [visible, setVisible] = useState("book");
  const navigate = useNavigate();

  let isMounted = true;

  const fetchOrderDetails = useCallback(async () => {
    try {
      const res = await getOrderdetails({ Email: userdetails?.Email });
      setOrderDetails(res.resdata);
    } catch (err) {
      console.error("Error fetching order details:", err);
    }
  }, [userdetails?.Email]);

  const fetchTestSeriesOrders = useCallback(async () => {
    try {
      if (userdetails?.id) {
        const res = await getUserOrders(userdetails.id);
        if (res.success) {
          setTestSeriesOrders(res.orders);
        }
      }
    } catch (err) {
      console.error("Error fetching test series orders:", err);
    }
  }, [userdetails?.id]);

  useEffect(() => {
    if (isMounted) {
      fetchOrderDetails();
      fetchTestSeriesOrders();
    }
    return () => (isMounted = false);
  }, [fetchOrderDetails, fetchTestSeriesOrders]);

  useEffect(() => {
    const toTopButton = document.getElementById("to-top-button");

    const handleScroll = () => {
      if (
        document.body.scrollTop > 500 ||
        document.documentElement.scrollTop > 500
      ) {
        toTopButton.classList.remove("hidden");
      } else {
        toTopButton.classList.add("hidden");
      }
    };

    const handleClick = () => {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    };

    if (toTopButton) {
      window.addEventListener("scroll", handleScroll);
      toTopButton.addEventListener("click", handleClick);
    }
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (toTopButton) {
        toTopButton.removeEventListener("click", handleClick);
      }
    };
  }, []);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const downloadPDF = async (orderId) => {
    setDownloadingPDF((prev) => ({ ...prev, [orderId]: true }));
    try {
      var resData = await apidownloadPDF(orderId);
      const pdfBlob = new Blob([resData], { type: "application/pdf" });
      const pdfFileName = `${orderId}.pdf`;
      saveAs(pdfBlob, pdfFileName);
    } catch (error) {
      console.error("Error downloading PDF:", error);
      toast.error("Failed to download PDF");
    } finally {
      setDownloadingPDF((prev) => ({ ...prev, [orderId]: false }));
    }
  };

  const downloadTestSeriesPDF = async (order) => {
    setDownloadingPDF((prev) => ({ ...prev, [order._id]: true }));
    try {
      var resData = await apiTestseriesdownloadPDF(order.razorpay_order_id);
      const pdfBlob = new Blob([resData], { type: "application/pdf" });
      const pdfFileName = `Invoice_${order.razorpay_order_id}.pdf`;
      saveAs(pdfBlob, pdfFileName);
      toast.success("Invoice downloaded successfully");
    } catch (error) {
      console.error("Error downloading PDF:", error);
      toast.error("Failed to download invoice");
    } finally {
      setDownloadingPDF((prev) => ({ ...prev, [order._id]: false }));
    }
  };

  return (
    <>
      <div className="max-w-5xl px-4 py-8 mx-auto">
        <h1 className="mb-6 text-3xl font-bold">My Orders</h1>

        <div className="flex border-b border-gray-300">
          <button onClick={() => setVisible("book")}
            className={`flex-1 py-2 text-center font-medium ${visible === "book" ? "border-b-2 border-blue-600 text-blue-600" : "text-gray-500 hover:text-blue-600"}`}
          >
            Books
          </button>
          <button onClick={() => setVisible("test")}
            className={`flex-1 py-2 text-center font-medium ${
              visible === "test" ? "border-b-2 border-blue-600 text-blue-600" : "text-gray-500 hover:text-blue-600"}`}
          >
            Test Series
          </button>
        </div>

        {visible === "book" && (
          <>
            {orderDetails.length > 0 ? (
              <div className="mt-6 space-y-6">
                {orderDetails.map((order) => (
                  <div key={order._id} className="p-6 bg-white rounded-lg shadow-md">
                    <div className="items-center justify-between mb-4 lg:flex">
                      <h2 className="text-xl font-semibold">Order ID: {order.Order_id}</h2>
                      <div className="flex gap-5">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${order.Payment_Status === "Paid" ? "bg-green-100 text-green-800": "bg-red-100 text-red-800"}`}>
                          {order.Payment_Status}
                        </span>
                        <button title="Download PDF" onClick={() => downloadPDF(order.Order_id)} className="inline-flex items-center text-xl font-medium text-red-600 gap-x-1 decoration-2"
                          disabled={downloadingPDF[order.Order_id]}
                        >
                          {downloadingPDF[order.Order_id] ? (
                            <i className="fas fa-spinner fa-spin"></i>
                          ) : (
                            <p className="px-3 py-1 text-sm text-black bg-yellow-200 rounded-xl">
                              Download
                            </p>
                          )}
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                      <div>
                        <p className="text-gray-600">Order Date:</p>
                        <p className="font-medium">
                          {new Date(order.Order_Date).toLocaleDateString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600">Total Amount:</p>
                        <p className="font-medium">₹{order.Total_Amount}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Billing Name:</p>
                        <p className="font-medium">{order.Billing_Name}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Email:</p>
                        <p className="font-medium">{order.Email}</p>
                      </div>
                      <div className="">
                        <p className="text-gray-600">Delivery Address:</p>
                        <p className="font-medium">{order.Delivery_Address}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Order Status:</p>
                        <p className="font-medium">{order.Order_Status}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Invoice ID:</p>
                        <p className="font-medium">{order.Invoice_ID}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Payment ID:</p>
                        <p className="font-medium">{order.Payment_Id}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center p-8 mt-6 bg-white rounded-lg shadow-md">
                <PackageX className="w-16 h-16 mb-4 text-gray-400" />
                <h3 className="mb-2 text-xl font-semibold text-gray-900">
                  No Orders Found
                </h3>
                <p className="text-center text-gray-600">
                  You haven&apos;t placed any orders yet. Start shopping to see
                  your orders here!
                </p>
              </div>
            )}
          </>
        )}

        {visible === "test" && (
          <>
            {testSeriesOrders.length > 0 ? (
              <div className="mt-6 space-y-6">
                {testSeriesOrders.map((order) => (
                  <div key={order._id} className="p-6 bg-white rounded-lg shadow-md">
                    <div className="items-center justify-between mb-4 lg:flex">
                      <h2 className="text-xl font-semibold">
                        {order.test_series_name}
                      </h2>
                      <div className="flex gap-2 mt-4 lg:gap-5 lg:mt-0">
                        <span className={`px-3 py-1 rounded-xl text-sm font-medium flex justify-center items-center ${
                            order.payment_status === "completed" ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"
                          }`}
                        >
                          {order.payment_status === "completed" ? "Paid" : order.payment_status}
                        </span>
                        {order.payment_status === "completed" && (
                          <button title="Download Invoice" onClick={() => downloadTestSeriesPDF(order)} className="inline-flex items-center text-xl font-medium text-red-600 gap-x-1 decoration-2"
                            disabled={downloadingPDF[order._id]}
                          >
                            {downloadingPDF[order._id] ? (
                              <i className="fas fa-spinner fa-spin"></i>
                            ) : (
                              <p className="px-3 py-1 text-sm text-black bg-yellow-200 rounded-xl">
                                Download Invoice
                              </p>
                            )}
                          </button>
                        )}
                        <button onClick={() =>navigate(`/online-test-series/${order.test_series_slug}`)} className="px-3 py-1 text-sm bg-blue-200 rounded-xl">
                          View test series
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                      <div>
                        <p className="text-gray-600">Order Date:</p>
                        <p className="font-medium">
                          {new Date(order.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600">Amount:</p>
                        <p className="font-medium">₹{order.amount}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Order ID:</p>
                        <p className="font-medium">{order.razorpay_order_id}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Payment ID:</p>
                        <p className="font-medium">
                          {order.razorpay_payment_id || "N/A"}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600">Test Series Name:</p>
                        <p className="font-medium">{order.test_series_name}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Status:</p>
                        <p className="font-medium capitalize">
                          {order.order_status}
                        </p>
                      </div>
                      {order.payment_date && (
                        <div>
                          <p className="text-gray-600">Payment Date:</p>
                          <p className="font-medium">
                            {new Date(order.payment_date).toLocaleDateString()}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center p-8 mt-6 bg-white rounded-lg shadow-md">
                <PackageX className="w-16 h-16 mb-4 text-gray-400" />
                <h3 className="mb-2 text-xl font-semibold text-gray-900">
                  No Test Series Purchased
                </h3>
                <p className="text-center text-gray-600">
                  You haven&apos;t purchased any test series yet. Browse our
                  test series to get started!
                </p>
              </div>
            )}
          </>
        )}
      </div>
      <div className="fixed z-50 flex flex-col items-end space-y-8 bottom-24 right-5">
        <div className="relative" onClick={toggleSidebar}>
          <img src="/images/shopping-cart.png" alt="Cart" className="cursor-pointer w-14 h-14"/>
          <span className="absolute flex items-center justify-center w-6 h-6 text-xs text-white bg-red-600 rounded-full -top-2 -right-2">
            {cart.length}
          </span>
        </div>

        <a href="https://wa.me/9047127777?text=Hello%20I%20want%20to%20enquire%20about%20your%20services" target="_blank" rel="noopener noreferrer">
          <Tooltip color="success" content="Contact on WhatsApp Now!" placement="left" showArrow={true}>
            <img src="/images/whatsapp.png" alt="WhatsApp" className="w-14 h-14 animate-bounce-up-down"/>
          </Tooltip>
        </a>
      </div>
      <button id="to-top-button" title="Go To Top"
        className="fixed z-50 hidden p-4 text-lg font-semibold text-white transition-colors duration-300 border-0 rounded-full shadow-md bottom-5 right-5 w-14 h-14 bg-regal-blue hover:bg-regal-yellow"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
          <path d="M12 4l8 8h-6v8h-4v-8H4l8-8z" />
        </svg>
      </button>
      <Cartsidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)}/>
    </>
  );
}
